import {
  AuthService,
  getNewErrors,
  IColor,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import { GalleryDisplayFundraisersType, IconType, MediaType } from "../enums";
import {
  IAPILinkedMediaModel,
  IGallerySettings,
} from "../interfaces/IGalleryModels";
import { autorun } from "mobx";
import { MediaItem } from "@mindmemobile/mm-shared-library";

export class GallerySettingsModel {
  id: string;
  accountId: string;
  title: string;
  iconMedia: MediaModel;
  icon: string;
  iconColor?: ColorModel;
  titleColor: ColorModel;
  backgroundColor: ColorModel;
  displayFundraisers: keyof typeof GalleryDisplayFundraisersType;
  timeframeDays: string;
  specificFundraiserIds: string[];
  iconType?: keyof typeof IconType;
  showLibrary: boolean;
  pageName?: string;
  errors: InputErrorModel<GallerySettingsModel>[];

  constructor(data?: Partial<GallerySettingsModel>) {
    this.id = data?.id || "";
    this.accountId = data?.accountId || "";
    this.title = data?.title || "";
    this.iconMedia = data?.iconMedia || new MediaModel();
    this.iconType = data?.iconType || IconType.None;
    this.icon = data?.icon || "Heart";
    this.showLibrary = data?.showLibrary || false;
    this.pageName = data?.pageName || "";
    this.iconColor = data?.iconColor
      ? new ColorModel(data.iconColor)
      : new ColorModel({
        rgba: "rgba(16, 185, 129, 1)",
        rgbValue: { r: "16", g: "185", b: "129", a: "1" },
        hex: "#10b981",
      });
    this.titleColor = data?.titleColor
      ? new ColorModel(data.titleColor)
      : new ColorModel({
        rgba: "rgba(30, 41, 59, 1)",
        rgbValue: { r: "30", g: "41", b: "59", a: "1" },
        hex: "#1e293b",
      });
    this.backgroundColor = data?.backgroundColor
      ? new ColorModel(data.backgroundColor)
      : new ColorModel({
        hex: "#f8fafc",
        rgba: "rgba(248, 250, 252, 1)",
        rgbValue: { r: "248", g: "250", b: "252", a: "1" },
      });
    this.displayFundraisers = data?.displayFundraisers ?? "AllActive";
    this.timeframeDays = data?.timeframeDays || "";
    this.specificFundraiserIds = data?.specificFundraiserIds || [];
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<GallerySettingsModel>(e);
        })) ||
      [];
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof GallerySettingsModel): boolean {
    return this.errors.some((error) => error.field === field);
  }

  validate(isAutoRun: boolean = false) {
    const newErrors: InputErrorModel<GallerySettingsModel>[] = [];
    if (this.iconType === IconType.Image) {
      if (!this.iconMedia || !this.iconMedia.validate()) {
        newErrors.push(
          new InputErrorModel<GallerySettingsModel>({
            field: "iconMedia",
            message: "Icon media is required",
          })
        );
      }
    }
    if (
      this.displayFundraisers === "AllActiveStarting" ||
      this.displayFundraisers === "RecentlyAdded"
    ) {
      if (!this.timeframeDays || parseFloat(this.timeframeDays) <= 0) {
        newErrors.push(
          new InputErrorModel<GallerySettingsModel>({
            field: "timeframeDays",
            message: "Timeframe Days must be a positive number",
          })
        );
      }
    }
    if (this.displayFundraisers === "Specific") {
      if (this.specificFundraiserIds.length === 0) {
        newErrors.push(
          new InputErrorModel<GallerySettingsModel>({
            field: "specificFundraiserIds",
            message: "At least one specific fundraiser ID must be selected",
          })
        );
      }
    }
    if (!this.title) {
      newErrors.push(
        new InputErrorModel<GallerySettingsModel>({
          field: "title",
          message: "Title is required",
        })
      );
    }
    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  static deserializeAPIModel(data: IGallerySettings): GallerySettingsModel {
    return new GallerySettingsModel({
      id: data.id,
      title: data.title,
      iconColor: data.iconColor
        ? ColorModel.deserializeAPIModel(data.iconColor)
        : new ColorModel(),
      iconMedia: data.iconMedia
        ? MediaModel.deserializeAPIModel(data.iconMedia)
        : new MediaModel(),
      titleColor: ColorModel.deserializeAPIModel(data.titleTextColor),
      backgroundColor: ColorModel.deserializeAPIModel(data.titleBgColor),
      displayFundraisers: data.fundRaiserDisplayType,
      timeframeDays: data.recentlyAddedDays?.toString(),
      specificFundraiserIds: data.fundRaiserIds,
      icon: data.icon,
      iconType: data.iconType as keyof typeof IconType,
      pageName: data.pageName,
      accountId: data.accountId,
    });
  }

  createAPIModel(): IGallerySettings {
    return {
      title: this.title,
      iconColor: this.iconColor ? this.iconColor.createAPIModel() : undefined,
      titleTextColor: this.titleColor
        ? this.titleColor.createAPIModel()
        : undefined,
      iconMedia: this.iconMedia?.mediaId
        ? this.iconMedia.createAPIModel()
        : undefined,
      icon: this.icon,
      iconType: this.iconType,
      titleBgColor: this.backgroundColor.createAPIModel(),
      fundRaiserDisplayType: this.displayFundraisers,
      fundRaiserIds: this.specificFundraiserIds,
      accountId: AuthService.authData.accountId,
      recentlyAddedDays:
        this.displayFundraisers === "AllActiveStarting" ||
          this.displayFundraisers === "RecentlyAdded"
          ? this.timeframeDays
            ? parseInt(this.timeframeDays)
            : 0
          : 0,
    };
  }

  createUpdateAPIModel(): IGallerySettings {
    return {
      ...this.createAPIModel(),
      id: this.id,
      userId: AuthService.mindmeAccountDetails?.userId,
    };
  }

  deserializeColor(color: ColorModel): IColor {
    return color as IColor;
  }
}

export class ColorModel {
  hex: string;
  isGradient: boolean;
  gradient: string;
  rgbValue: RGBA;
  rgba: string;
  constructor(data?: Partial<ColorModel>) {
    this.hex = data?.hex || "";
    this.isGradient = data?.isGradient || false;
    this.gradient = data?.gradient || "";
    this.rgbValue = data?.rgbValue || new RGBA();
    this.rgba = data?.rgba || "";
  }

  static fromString(color: string): ColorModel {
    const originalValue = color;
    const value = color.trim().toLowerCase();

    const fallback = new ColorModel({
      hex: "#000000",
      rgba: "rgba(0, 0, 0, 1)",
      rgbValue: new RGBA({ r: "0", g: "0", b: "0", a: "1" }),
    });

    if (!value) return fallback;
    if (/^#([0-9a-f]{3,8})$/.test(value)) {
      const hex = value.slice(1);

      let r = 0,
        g = 0,
        b = 0,
        a = 1;

      if (hex.length <= 4) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
        if (hex.length === 4) {
          a = parseInt(hex[3] + hex[3], 16) / 255;
        }
      } else {
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
        if (hex.length === 8) {
          a = parseInt(hex.slice(6, 8), 16) / 255;
        }
      }

      return new ColorModel({
        hex: originalValue,
        rgba: `rgba(${r}, ${g}, ${b}, ${+a.toFixed(3)})`,
        rgbValue: new RGBA({
          r: r.toString(),
          g: g.toString(),
          b: b.toString(),
          a: a.toString(),
        }),
      });
    }

    const rgbaMatch = value.match(
      /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|0?\.\d+|1))?\s*\)$/
    );

    if (rgbaMatch) {
      const r = +rgbaMatch[1];
      const g = +rgbaMatch[2];
      const b = +rgbaMatch[3];
      const a = rgbaMatch[4] !== undefined ? +rgbaMatch[4] : 1;

      if (r <= 255 && g <= 255 && b <= 255 && a >= 0 && a <= 1) {
        return new ColorModel({
          hex: ColorModel.rgbaToHex(`rgba(${r},${g},${b})`),
          rgba: `rgba(${r}, ${g}, ${b}, ${a})`,
          rgbValue: new RGBA({
            r: r.toString(),
            g: g.toString(),
            b: b.toString(),
            a: a.toString(),
          }),
        });
      }
    }

    return fallback;
  }

  private static normalizeHex(hex: string): string {
    if (hex.length === 4) {
      return (
        "#" +
        hex
          .substring(1)
          .split("")
          .map((c) => c + c)
          .join("")
      );
    }
    return hex.toLowerCase();
  }

  private static hexToRgba(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, 1)`;
  }

  private static rgbaToHex(rgba: string): string {
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!match) return "#000000";

    const [r, g, b] = match.slice(1, 4).map(Number);

    return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
  }

  static deserializeAPIModel(data?: IColor | null): ColorModel {
    if (!data) return new ColorModel();
    const mappedData: Partial<ColorModel> = {
      hex: data.hex,
      isGradient: data.isGradient,
      gradient: data.gradient,
      rgbValue: data.rgbValue,
      rgba: data.rgba,
    };
    return new ColorModel(mappedData);
  }

  createAPIModel(): IColor | null {
    if (!this || (!this.hex && !this.rgbValue && !this.rgba)) return null;
    return {
      hex: this.hex,
      isGradient: this.isGradient,
      gradient: this.gradient,
      rgbValue: this.rgbValue,
      rgba: this.rgba,
    };
  }
}

export class RGBA {
  r: string;
  g: string;
  b: string;
  a: string;
  constructor(data?: Partial<RGBA>) {
    this.r = data?.r || "";
    this.g = data?.g || "";
    this.b = data?.b || "";
    this.a = data?.a || "";
  }
}

export class MediaModel {
  fullURL: string;
  mediaId: string;
  s3Key: string;
  type: keyof typeof MediaType;
  isFavourite?: boolean;
  title?: string;
  description?: string;
  constructor(data?: Partial<MediaModel>) {
    this.fullURL = data?.fullURL || "";
    this.mediaId = data?.mediaId || "";
    this.s3Key = data?.s3Key || "";
    this.type = data?.type || MediaType.Image;
    this.isFavourite = data?.isFavourite || false;
    this.title = data?.title || "";
    this.description = data?.description || "";
  }

  static deserializeAPIModel(data: IAPILinkedMediaModel): MediaModel {
    const mappedData: Partial<MediaModel> = {
      fullURL: data?.fullURL || "",
      mediaId: data?.mediaId || "",
      s3Key: data?.s3Key,
      type: data?.type,
      isFavourite: data?.isFavourite || false,
      title: data?.title || "",
      description: data?.description || "",
    };
    return new MediaModel(mappedData);
  }

  static mediaItemToMediaModel(mediaItem: MediaItem): MediaModel {
    const mediaModel = new MediaModel({
      fullURL: mediaItem.url,
      mediaId: mediaItem.id,
      s3Key: mediaItem.s3Key || "",
      type: mediaItem.mediaType as keyof typeof MediaType,
      title: "",
      description: mediaItem.description || "",
    });
    if (mediaItem.regularURL) {
      mediaModel.fullURL = mediaItem.regularURL;
      mediaModel.s3Key = mediaItem.regularS3Key || "";
    }
    return mediaModel;
  }

  createAPIModel(): IAPILinkedMediaModel {
    return {
      fullURL: this.fullURL,
      mediaId: this.mediaId || null,
      s3Key: this.s3Key,
      type: this.type,
      isFavourite: this.isFavourite || false,
      name: this.title,
      title: this.title || "",
      description: this.description || "",
    };
  }

  isImage() {
    return this.type === MediaType.Image;
  }

  validate(): boolean {
    return this.mediaId?.length > 0 || this.fullURL?.length > 0;
  }
}
